import { defineStore } from 'pinia';
import { ref } from 'vue';
import { SimpleGlobalSettingDto, GlobalSettingsEnum } from '@/api';
import { GlobalSetting as GlobalSettingConstants } from '@/constants/GlobalSettting.ts';

const globalSettingFactory = (): SimpleGlobalSettingDto => {
  return {
    id: '',
    name: '',
    value: 0,
  } as SimpleGlobalSettingDto;
};

export const GLOBAL_SETTING_MIN_TICKET_DAYS_IN_ADVANCE_VALUE = 1;
export const GLOBAL_SETTING_MAX_TICKET_DAYS_IN_ADVANCE_VALUE = 10;
export const GLOBAL_SETTING_MIN_CARD_DAYS_IN_ADVANCE_VALUE = 30;
export const GLOBAL_SETTING_MAX_CARD_DAYS_IN_ADVANCE_VALUE = 90;
export const GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE = 30;
export const GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE = 90;

export const GLOBAL_SETTING_MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP = 90;

export const useGlobalSettingStore = defineStore('globalSetting', () => {
  const globalSettings = ref<Array<SimpleGlobalSettingDto>>([]);
  const customerCardValidity = ref<SimpleGlobalSettingDto>(globalSettingFactory());
  const customerProfileValidToInYears = ref<SimpleGlobalSettingDto>(globalSettingFactory());
  const customerNotificationMinCardDaysInAdvance = ref(GLOBAL_SETTING_MIN_CARD_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMaxCardDaysInAdvance = ref(GLOBAL_SETTING_MAX_CARD_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMinTicketDaysInAdvance = ref(GLOBAL_SETTING_MIN_TICKET_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMaxTicketDaysInAdvance = ref(GLOBAL_SETTING_MAX_TICKET_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMinCustomerProfileDaysInAdvance = ref(
    GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE,
  );
  const customerNotificationMaxCustomerProfileDaysInAdvance = ref(
    GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE,
  );
  const maxDaysBetweenCurrentDateAndTicketFareValidFromBeforeStartAtEshop = ref(
    GLOBAL_SETTING_MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP,
  );

  const setGlobalSettings = (payload: Array<SimpleGlobalSettingDto>) => {
    globalSettings.value = payload;
    customerCardValidity.value =
      payload.find(setting => setting.name === GlobalSettingConstants.CUSTOMER_CARD_VALIDITY) || globalSettingFactory();
    customerProfileValidToInYears.value =
      payload.find(setting => setting.name === GlobalSettingsEnum.CUSTOMER_PROFILE_DEFAULT_VALID_TO_IN_YEARS) ||
      globalSettingFactory();
    customerNotificationMinCardDaysInAdvance.value =
      payload.find(setting => setting.name === GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MIN_CARD_DAYS_IN_ADVANCE)
        ?.value ?? GLOBAL_SETTING_MIN_CARD_DAYS_IN_ADVANCE_VALUE;
    customerNotificationMaxCardDaysInAdvance.value =
      payload.find(setting => setting.name === GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MAX_CARD_DAYS_IN_ADVANCE)
        ?.value ?? GLOBAL_SETTING_MAX_CARD_DAYS_IN_ADVANCE_VALUE;
    customerNotificationMinTicketDaysInAdvance.value =
      payload.find(setting => setting.name === GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MIN_TICKET_DAYS_IN_ADVANCE)
        ?.value ?? GLOBAL_SETTING_MIN_TICKET_DAYS_IN_ADVANCE_VALUE;
    customerNotificationMaxTicketDaysInAdvance.value =
      payload.find(setting => setting.name === GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MAX_TICKET_DAYS_IN_ADVANCE)
        ?.value ?? GLOBAL_SETTING_MAX_TICKET_DAYS_IN_ADVANCE_VALUE;
    customerNotificationMinCustomerProfileDaysInAdvance.value =
      payload.find(
        setting => setting.name === GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE,
      )?.value ?? GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE;
    customerNotificationMaxCustomerProfileDaysInAdvance.value =
      payload.find(
        setting => setting.name === GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE,
      )?.value ?? GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE;
    maxDaysBetweenCurrentDateAndTicketFareValidFromBeforeStartAtEshop.value =
      payload.find(
        setting =>
          setting.name ===
          GlobalSettingsEnum.MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP,
      )?.value ?? GLOBAL_SETTING_MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP;
  };

  return {
    globalSettings,
    customerCardValidity,
    customerProfileValidToInYears,
    customerNotificationMinCardDaysInAdvance,
    customerNotificationMaxCardDaysInAdvance,
    customerNotificationMinTicketDaysInAdvance,
    customerNotificationMaxTicketDaysInAdvance,
    customerNotificationMinCustomerProfileDaysInAdvance,
    customerNotificationMaxCustomerProfileDaysInAdvance,
    maxDaysBetweenCurrentDateAndTicketFareValidFromBeforeStartAtEshop,

    setGlobalSettings,
  };
});
